import { Icon, Panel } from '@dayon/design-system';

/**
 * What the design shows that this console still cannot source.
 *
 * The panel used to carry nine notices. Seven of them described controls that
 * are now built, and a note that has stopped being true is worse than no note —
 * it teaches a reader to skim the panel. Those seven are gone; what remains is
 * the three that are still true, each stating the thing that is actually
 * missing rather than the feature it would have enabled.
 */
export function NotYetAvailable({ scopedBrandName }: { scopedBrandName: string | null }) {
  return (
    <Panel title="Not yet available">
      <div className="stack">
        <div className="notice notice-quiet" id="access-sms-gap">
          <Icon name="info" />
          <div>
            {/* No SMS factor table exists in the identity schema, no per-role factor policy, and
                no SMS provider is integrated (services/api backoffice/platform.ts says the same). */}
            <strong>SMS as a second factor</strong> — the invite form shows the option, switched
            off. Text-message sign-in codes are not available yet: the platform cannot keep a phone
            as a sign-in method, has no rule such as &ldquo;SMS is allowed only for Support&rdquo;,
            and has no text-message provider connected. Offering a method that could not be kept or
            delivered would make the invite promise something the platform cannot do. The
            authenticator app is real and is set up on every acceptance, so the roster&rsquo;s 2FA
            column has three honest states — App, Pending setup and None — and no fourth.
          </div>
        </div>
        <div className="notice notice-quiet" id="access-mailer-gap">
          <Icon name="info" />
          <div>
            {/* MAIL_DRIVER picks SES or the simulator; MAIL_OUTBOUND_MODE picks who may receive.
                Local development ships SUPPRESS (.env.example); staging ships LIVE
                (deploy/staging.env.example), so "staging suppresses" is no longer true. */}
            <strong>Whether mail actually leaves</strong> — the platform can send now: an invite
            link records a real message at the moment the link is made, and the platform hands it
            to the mail service a minute later. What this console cannot promise is DELIVERY. How
            this installation is set up for mail decides it — whether it uses a real mail service
            at all, and who may receive anything — and a test installation can be set to record
            every message and send none. So the invite dialog reports the status of the message it
            just wrote rather than claiming a send, and it shows you the link either way.{' '}
            <strong>A factor reset still tells the admin nothing</strong>, and that is deliberate
            rather than missing: it transmits no secret, and they enrol from their own screen at
            their next sign-in.
          </div>
        </div>
        <div className="notice notice-quiet" id="access-retention-gap">
          <Icon name="info" />
          <div>
            {/* No table carries a retention_until or legal-hold column, and the hash-chained
                audit log has no partitioning plan for seven years. */}
            <strong>Seven-year retention</strong> — &ldquo;Retained seven years · exportable to CSV
            by any role holding audit.export&rdquo; was the plan, and only the export half is
            built. Nothing records how long an entry must be kept or puts an entry on legal hold,
            and there is no plan yet for holding seven years of the chained log, so nothing on this
            platform enforces or measures the retention. Treat that half as intent.
          </div>
        </div>
        <div className="notice notice-quiet" id="access-brand-filter-note">
          <Icon name="info" />
          <div>
            <strong>The global brand filter</strong> — correct as it is, and stated so it does not
            read as an oversight. Nothing on this screen narrows by brand, because identity is
            platform-wide by design: an admin&rsquo;s brand scope restricts what they may SEE, it
            does not file the admin under a brand.{' '}
            {scopedBrandName
              ? `The global scope is set to ${scopedBrandName}; this module ignores it, and the Brand scope column shows each admin's own restriction instead.`
              : 'The global brand filter cannot narrow anything on this screen.'}
          </div>
        </div>
      </div>
    </Panel>
  );
}
